import axios from "axios";
import { saveAs } from "file-saver";
import {
  uploadFormDataWithProgress,
  prepareFormDataForStreaming,
} from "./streamingUpload";
import { handleLogoutTokenExpired } from "../../utils/utilityFunctions";

const API = axios.create({
  baseURL: "/api/eval_bot",
});

// Track active upload XHR for cancellation support
let activeUploadXhr = null;

/**
 * @typedef {Object} ValidationRule
 * @prop {(value: any) => boolean}   check     – a predicate that returns true if the field is OK
 * @prop {string}                     message   – the error message to show if it fails
 */

/**
 * Runs all of your rules against the entries in a FormData.
 * Throws a ValidationError if any rule fails.
 */
class ValidationError extends Error {
  constructor(errors) {
    super("Validation failed");
    this.name = "ValidationError";
    this.errors = errors; // array of { field, message }
  }
}

const validateFormData = (formData, rulesByField) => {
  const errors = [];
  for (const [field, rules] of Object.entries(rulesByField)) {
    const raw = formData.get(field);
    for (const { check, message } of rules) {
      if (!check(raw)) {
        errors.push({ field, message });
        break; // stop at first failure per field
      }
    }
  }
  if (errors.length) {
    throw new ValidationError(errors);
  }
};

//create bot
/**
 * Creates a bot using the existing approach with streaming upload for files
 * @param {FormData} botData - The form data containing bot configuration and files
 * @param {String} token - Authentication token
 * @param {Object} options - Additional options
 * @param {Function} options.onProgress - Optional callback for progress updates
 * @param {Function} options.onError - Optional callback for error handling
 * @returns {Promise} - Resolves with created bot data
 */
const createBot = async (botData, token, options = {}) => {
  const { onProgress, onError } = options;

  // 1) VALIDATION STEP
  try {
    validateFormData(botData, {
      name: [
        {
          check: (v) => typeof v === "string" && v.trim().length > 0,
          message: "Bot name must not be empty.",
        },
      ],
      workflow: [
        {
          check: (v) => {
            try {
              JSON.parse(v);
              return true;
            } catch {
              return false;
            }
          },
          message: "Workflow must be valid JSON.",
        },
      ],
      // add more fields & rules here as needed...
    });
  } catch (err) {
    if (err instanceof ValidationError) {
      // aggregate the messages
      const msg = err.errors.map((e) => `${e.field}: ${e.message}`).join("\n");
      if (onError) onError(msg);
      // short-circuit
      return Promise.reject({
        status: "validation_error",
        details: err.errors,
      });
    }
    throw err;
  }

  if (!token) {
    token = JSON.parse(localStorage.getItem("token"));
  }
  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__.token;
  }

  if (!token) {
    handleLogoutTokenExpired();
    return;
  }
  try {
    // Check if formData contains any files
    let hasFiles = false;
    // Determine if we need to use streaming approach
    let useStreaming = false;

    if (botData.has("datasets")) {
      const datasets = botData.get("datasets");
      try {
        // Try to parse as JSON if it's a string
        const parsedDatasets =
          typeof datasets === "string" ? JSON.parse(datasets) : datasets;

        // Check if any dataset has filedata
        if (Array.isArray(parsedDatasets)) {
          hasFiles = parsedDatasets.some(
            (d) => d.filedata || d instanceof File
          );
        } else if (typeof parsedDatasets === "object") {
          // Handle object format (indexed object)
          hasFiles = Object.values(parsedDatasets).some(
            (d) => d.filedata || d instanceof File
          );
        }
      } catch (e) {
        console.log("Error parsing datasets:", e);
        return;
      }
    }

    // Check for file inputs directly
    for (const [key, value] of botData.entries()) {
      if (value instanceof File || (key.startsWith("file") && value)) {
        hasFiles = true;
        break;
      }
    }

    useStreaming = hasFiles;

    // Convert botData to the right format if needed
    let formData = botData;
    if (!(botData instanceof FormData)) {
      formData = prepareFormDataForStreaming(botData);
      useStreaming = true;
    }

    // Decide which approach to use
    if (useStreaming) {
      return await uploadFormDataWithProgress(
        formData,
        token,
        "/api/eval_bot/create",
        {
          onProgress,
          onError,
          setXhrRef: (xhr) => {
            activeUploadXhr = xhr;
          },
          onAbort: () => {
            activeUploadXhr = null;
          },
        }
      );
    } else {
      const response = await API.post("/create", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`,
        },
      });
      return response.data;
    }
  } catch (error) {
    if (error.response.status.includes("Bot name already exists")) {
      return "Bot name already exists";
    }

    if (error.response.status === 403) {
      handleLogoutTokenExpired();
    }
    throw new Error(error.response.data.error);
  }
};

/**
 * Cancels an active bot creation upload if one exists
 */
const cancelBotUpload = () => {
  if (activeUploadXhr) {
    activeUploadXhr.abort();
    activeUploadXhr = null;
    return true;
  }
  return false;
};

//get_bot by creator
const getBotbyOrg = async (id, token) => {
  if (!token) {
    token = JSON.parse(localStorage.getItem("token"));
  }
  const __USER__ = JSON.parse(localStorage.getItem("user"));

  if (!token || token !== __USER__.token) {
    token = __USER__.token;
  }
  if (!token) {
    handleLogoutTokenExpired();
    return;
  }
  if (!id) {
    console.error("Invalid data identification.");
    throw new Error("Invalid data identification.");
  }

  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
  let response;
  try {
    response = await API.get(`/${id}`, config);
    return response.data;
  } catch (error) {
    if (error.response.status === 403) {
      handleLogoutTokenExpired();
    }
    throw new Error(error.response.data.error);
  }
};

//get_bot by creator templates
const getBotbyOrgTemplates = async (id, token) => {
  if (!token) {
    token = JSON.parse(localStorage.getItem("token"));
  }
  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__.token;
  }

  if (!token) {
    handleLogoutTokenExpired();
    return;
  }
  // id = null;
  if (!id) {
    console.error("Invalid data identification.");
    return "Organization id invalid";
  }

  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
  try {
    const response = await API.get(`/templates/${id}`, config);
    return response.data;
  } catch (error) {
    if (error.response.status === 403) {
      handleLogoutTokenExpired();
    }
    throw new Error(error.response.data.error);
  }
};

//save combination id
const saveOptimizedBot = async (ids, token) => {
  if (!token) {
    token = JSON.parse(localStorage.getItem("token"));
  }
  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__.token;
  }

  if (!token) {
    handleLogoutTokenExpired();
    return;
  }

  if (!ids) {
    console.error("Invalid data. Please refresh and try again");
    return;
  }

  const { combination_id, org_id, botID } = ids;

  if (!combination_id || !org_id || !botID) {
    console.error("Invalid data. Please refresh and try again");
    return;
  }
  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
  const response = await API.get(
    `/save/optimized/${combination_id}/${org_id}/${botID}`,
    config
  );
  return response.data;
};

//get combinations
// Get combination filter values
const getCombinationFilters = async (retrievalId, token) => {
  if (!retrievalId) {
    console.error("Invalid retrieval ID. Please refresh and try again");
    return;
  }
  
  if (!token) {
    token = JSON.parse(localStorage.getItem("token"));
  }
  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__.token;
  }

  //final token check
  if (!token) {
    handleLogoutTokenExpired();
    return;
  }
  
  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: "application/json",
    },
  };

  try {
    const response = await API.get(
      `/combination/filters/${retrievalId}`,
      config
    );
    return response.data;
  } catch (error) {
    if (error.response?.status === 403) {
      handleLogoutTokenExpired();
    }
    throw new Error(error.response?.data?.error || "Failed to fetch filter values");
  }
};

const getCombinations = async (sentData, token) => {
  //check for every data
  if (!sentData) {
    console.error("Invalid data. Please refresh and try again");
    return;
  }
  const { id, items, from, data } = sentData;
  // check for spread data
  if (!id || !items || !(from === 0 || from > 0) || data === null) {
    console.error("Invalid data. Please refresh and try again");
    return;
  }
  if (!token) {
    token = JSON.parse(localStorage.getItem("token"));
  }
  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__.token;
  }

  //final token check
  if (!token) {
    handleLogoutTokenExpired();
    return;
  }
  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: "application/json",
    },
  };
  if (data?.["sortOrder"] || data?.["filters"]) {
    try {
      const response = await API.post(
        `/combinations/${id}/${items}/${from}`,
        data,
        config
      );
      return response.data;
    } catch (error) {
      if (error.response.status === 403) {
        handleLogoutTokenExpired();
      }
      throw new Error(error.response.data.error);
    }
  } else {
    try {
      const response = await API.post(
        `/combinations/${id}/${items}/${from}`,
        {},
        config
      );
      return response.data;
    } catch (error) {
      console.log("Error getting combinations: ", error);
      if (error.response.status === 403) {
        handleLogoutTokenExpired();
      }
      throw new Error(error.response.data.error);
    }
  }
};

//get combinations
const getgenerationCombinations = async (id, token) => {
  if (!id) {
    console.error("Invalid ID. Please refresh and try again.");
    throw new Error("Invalid data. Please refresh and try again.");
  }

  if (!token) {
    token = JSON.parse(localStorage.getItem("token"));
  }

  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__?.token;
  }

  if (!token) {
    handleLogoutTokenExpired();
    return;
  }

  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  try {
    const response = await API.get(`/generation_combinations/${id}`, config);
    return response.data?.combinations || [];
  } catch (error) {
    console.error("Error fetching generation combinations:", error);
    if (error.response.status === 403) {
      handleLogoutTokenExpired();
    }
    throw new Error(error.response.data.error);
  }
};

//get combinations
const getFinetuningCombinations = async (id, token) => {
  if (!id) {
    console.error("Invalid ID. Please refresh and try again.");
    throw new Error("Invalid data. Please refresh and try again.");
  }

  if (!token) {
    token = JSON.parse(localStorage.getItem("token"));
  }

  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__?.token;
  }

  if (!token) {
    handleLogoutTokenExpired();
    return;
  }

  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  try {
    const response = await API.get(
      `/generation_combinations/finetune/${id}`,
      config
    );
    return response.data?.combinations || [];
  } catch (error) {
    console.error("Error fetching finetuning combinations:", error);
    if (error.response.status === 403) {
      handleLogoutTokenExpired();
    }
    throw new Error(error.response.data.error);
  }
};

//get parser combinations
const getParserCombinations = async (id, token) => {
  if (!id) {
    console.error("Invalid ID. Please refresh and try again.");
    throw new Error("Invalid data. Please refresh and try again.");
  }

  if (!token) {
    token = JSON.parse(localStorage.getItem("token"));
  }

  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__?.token;
  }

  if (!token) {
    handleLogoutTokenExpired();
    return;
  }

  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  try {
    const response = await API.get(`/parser_combinations/${id}`, config);
    return response.data?.combinations || [];
  } catch (error) {
    console.error("Error fetching parser combinations:", error);
    if (error.response.status === 403) {
      handleLogoutTokenExpired();
    }
    throw new Error(error.response.data.error);
  }
};

//run evals
const runEvals = async (ids, token) => {
  if (!ids) {
    console.error("Invalid data. Please refresh and try again.");
    throw new Error("Invalid data. Please refresh and try again.");
  }

  const { orgId, user_id, bot_id, combination_id, retrieval_id } = ids;
  if (!orgId || !user_id || !bot_id || !combination_id || !retrieval_id) {
    console.error(
      "Missing required identifiers. Please refresh and try again."
    );
    return;
  }

  if (!token) {
    token = JSON.parse(localStorage.getItem("token"));
  }

  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__?.token;
  }

  if (!token) {
    handleLogoutTokenExpired();
    return;
  }

  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  try {
    const response = await API.get(
      `/runeval/retrieval/${orgId}/${user_id}/${bot_id}/${retrieval_id}/${combination_id}`,
      config
    );
    return response.data;
  } catch (error) {
    console.error("Error running retrieval evals:", error);
    if (error.response.status === 403) {
      handleLogoutTokenExpired();
    }
    throw new Error(error.response.data.error);
  }
};

//run generation evals
const runGenerationEvals = async (ids, token) => {
  if (!ids) {
    console.error("Invalid data. Please refresh and try again.");
    throw new Error("Invalid data. Please refresh and try again.");
  }

  const {
    orgId,
    user_id,
    bot_id,
    combination_id,
    retrieval_combination,
    parser_combination,
    generation_id,
  } = ids;
  if (
    !orgId ||
    !user_id ||
    !bot_id ||
    !combination_id ||
    !generation_id ||
    !retrieval_combination ||
    !parser_combination
  ) {
    console.error(
      "Missing required identifiers. Please refresh and try again."
    );
    return;
  }

  if (!token) {
    token = JSON.parse(localStorage.getItem("token"));
  }

  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__?.token;
  }

  if (!token) {
    handleLogoutTokenExpired();
    return;
  }

  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
  try {
    const response = await API.post(
      `/runeval/generation/${orgId}/${user_id}/${bot_id}/${generation_id}/${combination_id}`,
      { retrieval_combination, parser_combination },
      config
    );
    return response.data;
  } catch (error) {
    console.error("Error running generation evals:", error);
    if (error.response.status === 403) {
      handleLogoutTokenExpired();
    }
    throw new Error(error.response.data.error);
  }
};

//run finetuning evals
const runFinetuningEvals = async (ids, token) => {
  if (!ids) {
    console.error("Invalid data. Please refresh and try again.");
    throw new Error("Invalid data. Please refresh and try again.");
  }

  const { orgId, user_id, bot_id, combination_id } = ids;
  if (!orgId || !user_id || !bot_id || !combination_id) {
    console.error(
      "Missing required identifiers. Please refresh and try again."
    );
    return;
  }

  if (!token) {
    token = JSON.parse(localStorage.getItem("token"));
  }

  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__?.token;
  }

  if (!token) {
    handleLogoutTokenExpired();
    return;
  }

  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  try {
    const response = await API.get(
      `/runeval/generation/finetune/${orgId}/${user_id}/${bot_id}/${combination_id}`,
      config
    );
    return response.data;
  } catch (error) {
    console.error("Error running finetuning evals:", error);
    if (error.response.status === 403) {
      handleLogoutTokenExpired();
    }
    throw new Error(error.response.data.error);
  }
};

//run start finetuning
const startFinetuning = async (ids, token) => {
  if (!ids) {
    console.error("Invalid data. Please refresh and try again.");
    throw new Error("Invalid data. Please refresh and try again.");
  }

  const { orgId, user_id, bot_id } = ids;
  if (!orgId || !user_id || !bot_id) {
    console.error(
      "Missing required identifiers. Please refresh and try again."
    );
    return;
  }

  if (!token) {
    token = JSON.parse(localStorage.getItem("token"));
  }

  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__?.token;
  }

  if (!token) {
    handleLogoutTokenExpired();
    return;
  }

  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  try {
    const response = await API.get(
      `/init/finetune/${orgId}/${user_id}/${bot_id}`,
      config
    );
    return response.data;
  } catch (error) {
    console.error("Error starting finetuning:", error);
    if (error.response.status === 403) {
      handleLogoutTokenExpired();
    }
    throw new Error(error.response.data.error);
  }
};

//get llm finetuning status
const getFinetuningStatus = async (ids, token) => {
  if (!ids) {
    console.error("Invalid data. Please refresh and try again.");
    throw new Error("Invalid data. Please refresh and try again.");
  }

  const { bot_id, combination_id } = ids;
  if (!bot_id || !combination_id) {
    console.error(
      "Missing required identifiers. Please refresh and try again."
    );
    return;
  }

  if (!token) {
    token = JSON.parse(localStorage.getItem("token"));
  }

  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__?.token;
  }

  if (!token) {
    handleLogoutTokenExpired();
    return;
  }

  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  try {
    const response = await API.get(
      `/get/finetune/status/${bot_id}/${combination_id}`,
      config
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching finetuning status:", error);
    if (error.response.status === 403) {
      handleLogoutTokenExpired();
    }
    throw new Error(error.response.data.error);
  }
};

//Generate prompts
const generatePrompts = async (bot_id, token) => {
  if (!bot_id) {
    console.error("Invalid bot ID. Please refresh and try again.");
    throw new Error("Invalid data. Please refresh and try again.");
  }

  if (!token) {
    token = JSON.parse(localStorage.getItem("token"));
  }

  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__?.token;
  }

  if (!token) {
    handleLogoutTokenExpired();
    return;
  }

  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  try {
    const response = await API.get(`/generate/mutations/${bot_id}`, config);
    localStorage.setItem("prompts", JSON.stringify(response.data));
    return response.data;
  } catch (error) {
    console.error("Error generating prompts:", error);
    if (error.response.status === 403) {
      handleLogoutTokenExpired();
    }
    throw new Error(error.response.data.error);
  }
};

//Generate prompts
const generateQueryWithProblemStatement = async (data, token) => {
  if (!data || typeof data !== "object") {
    console.error("Invalid data provided. Please refresh and try again.");
    throw new Error("Invalid data. Please refresh and try again.");
  }

  if (!token) {
    token = JSON.parse(localStorage.getItem("token"));
  }

  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__?.token;
  }

  if (!token) {
    handleLogoutTokenExpired();
    return;
  }

  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  try {
    const response = await API.post(
      `/generate/query_with_problems`,
      data,
      config
    );
    return response.data;
  } catch (error) {
    console.error("Error generating query with problem statement:", error);
    if (error.response.status === 403) {
      handleLogoutTokenExpired();
    }
    throw new Error(error.response.data.error);
  }
};

//run evals
const generateBasePrompt = async (problem_statement, token) => {
  if (!problem_statement || typeof problem_statement !== "string") {
    console.error(
      "Invalid problem statement provided. Please refresh and try again."
    );
    return;
  }

  if (!token) {
    token = JSON.parse(localStorage.getItem("token"));
  }

  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__?.token;
  }

  if (!token) {
    handleLogoutTokenExpired();
    return;
  }

  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  try {
    const response = await API.get(
      `/generate/mutations/${problem_statement}`,
      config
    );
    localStorage.setItem("prompts", JSON.stringify(response.data));
    return response.data;
  } catch (error) {
    console.error("Error generating base prompt:", error);
    if (error.response.status === 403) {
      handleLogoutTokenExpired();
    }
    throw new Error(error.response.data.error);
  }
};

//run evals
const initializeCombinations = async (data, token) => {
  if (!data || typeof data !== "object") {
    console.error("Invalid data provided. Please refresh and try again.");
    throw new Error("Invalid data. Please refresh and try again.");
  }

  const { user_id, bot_id, orgId, toolId } = data;
  if (!user_id || !bot_id || !orgId || !toolId) {
    console.error(
      "Missing required identifiers. Please refresh and try again."
    );
    return;
  }

  if (!token) {
    token = JSON.parse(localStorage.getItem("token"));
  }

  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__?.token;
  }

  if (!token) {
    handleLogoutTokenExpired();
    return;
  }

  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  try {
    const response = await API.get(
      `/init/${orgId}/${user_id}/${bot_id}/${toolId}`,
      config
    );
    return response.data;
  } catch (error) {
    console.error("Error initializing combinations:", error);
    if (error.response.status === 403) {
      handleLogoutTokenExpired();
    }
    throw new Error(error.response.data.error);
  }
};

//init sync
const initializeCombinationsSync = async (data, token) => {
  if (!data || typeof data !== "object") {
    console.error("Invalid data provided. Please refresh and try again.");
    throw new Error("Invalid data. Please refresh and try again.");
  }

  const { user_id, bot_id, orgId, toolId } = data;
  if ((!user_id || !bot_id || !orgId, !toolId)) {
    console.error(
      "Missing required identifiers. Please refresh and try again."
    );
    return;
  }

  if (!token) {
    token = JSON.parse(localStorage.getItem("token"));
  }

  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__?.token;
  }

  if (!token) {
    handleLogoutTokenExpired();
    return;
  }

  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  try {
    const response = await API.get(
      `/init/sync/${orgId}/${user_id}/${bot_id}/${toolId}`,
      config
    );
    return response.data;
  } catch (error) {
    console.error("Error initializing combinations (sync):", error);
    if (error.response.status === 403) {
      handleLogoutTokenExpired();
    }
    throw new Error(error.response.data.error);
  }
};

//run evals
const initializeEmbeddingFinetuning = async (data, token) => {
  if (!data || typeof data !== "object") {
    console.error("Invalid data provided. Please refresh and try again.");
    throw new Error("Invalid data. Please refresh and try again.");
  }

  const { comb_id, bot_id, orgId } = data;
  if (!comb_id || !bot_id || !orgId) {
    console.error(
      "Missing required identifiers. Please refresh and try again."
    );
    return;
  }

  if (!token) {
    token = JSON.parse(localStorage.getItem("token"));
  }

  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__?.token;
  }

  if (!token) {
    handleLogoutTokenExpired();
    return;
  }

  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  try {
    const response = await API.post(
      `/load_embedding_ft_model/${orgId}/${bot_id}/${comb_id}`,
      config
    );
    return response.data;
  } catch (error) {
    console.error("Error initializing embedding finetuning:", error);
    if (error.response.status === 403) {
      handleLogoutTokenExpired();
    }
    throw new Error(error.response.data.error);
  }
};

//Initialize Mutations
const initializeMutations = async (data, token) => {
  if (!data || typeof data !== "object") {
    console.error("Invalid data provided. Please refresh and try again.");
    throw new Error("Invalid data. Please refresh and try again.");
  }

  const { bot_id } = data;
  if (!bot_id) {
    console.error("Missing bot ID. Please refresh and try again.");
    throw new Error("Invalid data. Please refresh and try again.");
  }

  if (!token) {
    token = JSON.parse(localStorage.getItem("token"));
  }

  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__?.token;
  }

  if (!token) {
    handleLogoutTokenExpired();
    return;
  }

  const config = {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  };

  try {
    const response = await API.post(`/init/mutations/${bot_id}`, data, config);
    return response.data;
  } catch (error) {
    console.error("Error initializing mutations:", error);
    if (error.response.status === 403) {
      handleLogoutTokenExpired();
    }
    throw new Error(error.response.data.error);
  }
};

//Get Selected Combinations
const get_selected_combinations = async (bot_id, token) => {
  if (!bot_id) {
    console.error("Missing bot_id. Please refresh and try again.");
    throw new Error("Invalid data. Please refresh and try again.");
  }

  if (!token) {
    token = JSON.parse(localStorage.getItem("token"));
  }

  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__?.token;
  }

  if (!token) {
    handleLogoutTokenExpired();
    return;
  }

  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  try {
    const response = await API.get(`/selectedCombinations/${bot_id}`, config);
    return response.data;
  } catch (error) {
    console.error("Error fetching selected combinations:", error);
    if (error.response.status === 403) {
      handleLogoutTokenExpired();
    }
    throw new Error(error.response.data.error);
  }
};

//Get Selected Combinations
const makeDefault = async (data, token) => {
  if (!data || typeof data !== "object") {
    console.error("Invalid data provided. Please refresh and try again.");
    throw new Error("Invalid data. Please refresh and try again.");
  }

  const { bot_id, combination_id } = data;
  if (!bot_id || !combination_id) {
    console.error(
      "Missing bot_id or combination_id. Please refresh and try again."
    );
    return;
  }

  if (!token) {
    token = JSON.parse(localStorage.getItem("token"));
  }

  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__?.token;
  }

  if (!token) {
    handleLogoutTokenExpired();
    return;
  }

  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  try {
    const response = await API.get(
      `/makedefault/${bot_id}/${combination_id}`,
      config
    );
    return response.data;
  } catch (error) {
    console.error("Error in makeDefault:", error);
    if (error.response.status === 403) {
      handleLogoutTokenExpired();
    }
    throw new Error(error.response.data.error);
  }
};

//delete chatbot
const deleteChatbot = async (id, token) => {
  if (!id) {
    console.error("Missing chatbot ID. Please refresh and try again.");
    throw new Error("Invalid data. Please refresh and try again.");
  }

  if (!token) {
    token = JSON.parse(localStorage.getItem("token"));
  }

  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__?.token;
  }

  if (!token) {
    handleLogoutTokenExpired();
    return;
  }

  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  };

  try {
    const response = await API.delete(`/combinationset/${id}`, config);
    return response.data;
  } catch (error) {
    console.error("Error deleting chatbot:", error);
    if (error.response.status === 403) {
      handleLogoutTokenExpired();
    }
    throw new Error(error.response.data.error);
  }
};

//delete files
const deleteFiles = async (ids, token) => {
  if (!ids || typeof ids !== "object") {
    console.error("Invalid input data. Please refresh and try again.");
    throw new Error("Invalid data. Please refresh and try again.");
  }

  const { bot_id, retrieval_id, fileName } = ids;
  if (!bot_id || !retrieval_id || !fileName) {
    console.error("Missing bot_id, retrieval_id or fileName. Please refresh and try again.");
    throw new Error("Invalid data. Please refresh and try again.");
  }

  if (!token) {
    token = JSON.parse(localStorage.getItem("token"));
  }

  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__?.token;
  }

  if (!token) {
    handleLogoutTokenExpired();
    return;
  }

  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  };

  try {
    const response = await API.delete(`/deletefile/${bot_id}/${retrieval_id}/${fileName}`, config);
    return response.data;
  } catch (error) {
    console.error("Error deleting file:", error);
    if (error.response.status === 403) {
      handleLogoutTokenExpired();
    }
    throw new Error(error.response.data.error);
  }
};

//upload sources
const uploadSources = async (data, token, options = {}) => {
  const { formData } = data;
  const { orgId, user_id, bot_id, retrieval_id } = data.ids;
  if (!token) {
    token = JSON.parse(localStorage.getItem("token"));
  }
  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__?.token;
  }
  if (!token) {
    handleLogoutTokenExpired();
    return;
  }

  const { onProgress, onError } = options;
  const apiUrl = `/api/eval_bot/update/${orgId}/${user_id}/${bot_id}/${retrieval_id}`;

  try {
    // Check if formData contains any files
    let hasFiles = false;

    // Check for file inputs directly
    for (const [key, value] of formData.entries()) {
      if (
        value instanceof File ||
        value instanceof Blob ||
        (key.startsWith("file_") && value)
      ) {
        hasFiles = true;
        break;
      }
    }

    // Decide which upload approach to use
    if (hasFiles) {
      return await uploadFormDataWithProgress(formData, token, apiUrl, {
        onProgress,
        onError,
        setXhrRef: (xhr) => {
          // Store the XHR reference to enable abort functionality
          window.activeUploadXhr = xhr;
        },
        onAbort: () => {
          window.activeUploadXhr = null;
        },
      });
    } else {
      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      };

      const response = await API.post(apiUrl, formData, config);
      return response.data;
    }
  } catch (error) {
    console.error("Error uploading sources:", error);
    if (error.response.status === 403) {
      handleLogoutTokenExpired();
    }
    throw new Error(error.response.data.error);
  }
};

//get data types
const getDataTypes = async (data, token) => {
  // Validate input data
  if (!data) {
    console.error("Invalid input. Please provide the required data.");
    return;
  }

  // Get token from localStorage if not provided
  if (!token) {
    token = JSON.parse(localStorage.getItem("token"));
  }

  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__?.token;
  }

  // Final token check
  if (!token) {
    handleLogoutTokenExpired();
    return;
  }

  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  try {
    const response = await API.post("/datatypes", data, config);
    return response.data;
  } catch (error) {
    console.error("Error fetching data types:", error);
    if (error.response.status === 403) {
      handleLogoutTokenExpired();
    }
    throw new Error(error.response.data.error);
  }
};

//get meta data description
const getMetadataDescription = async (data, token) => {
  // Validate input data
  if (!data || !data.userId) {
    console.error("Invalid data. Please refresh and try again.");
    throw new Error("Invalid data. Please refresh and try again.");
  }

  const userId = data.userId;

  // Get token from localStorage if not provided
  if (!token) {
    token = JSON.parse(localStorage.getItem("token"));
  }

  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__?.token;
  }

  // Final token check
  if (!token) {
    handleLogoutTokenExpired();
    return;
  }

  try {
    const hasFile = data.fileData instanceof File;

    if (hasFile) {
      const formData = new FormData();
      formData.append("file", data.fileData);
      formData.append("columnTypes", JSON.stringify(data.columnTypes || {}));
      formData.append("filename", data.filename || "");

      return await uploadFormDataWithProgress(
        formData,
        token,
        `/api/eval_bot/generate-metadata-description/${userId}`
      );
    } else {
      if (!data.requestData) {
        console.error("Missing requestData for metadata description.");
        return;
      }

      const config = {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      };

      const response = await API.post(
        `/generate-metadata-description/${userId}`,
        JSON.stringify(data.requestData),
        config
      );

      return response.data;
    }
  } catch (error) {
    console.error("Error generating metadata:", error);
    if (error.response.status === 403) {
      handleLogoutTokenExpired();
    }
    throw new Error(error.response.data.error);
  }
};

//get default combinations
// Get default combinations
const getDefaultCombinations = async (data, token) => {
  if (!data || !data.user_id || !data.bot_id) {
    console.error("Invalid data. Please refresh and try again.");
    throw new Error("Invalid data. Please refresh and try again.");
  }

  if (!token) {
    token = JSON.parse(localStorage.getItem("token"));
  }

  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__?.token;
  }

  if (!token) {
    handleLogoutTokenExpired();
    return;
  }

  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  try {
    const response = await API.post(
      `/getdefualt/${data.user_id}/${data.bot_id}`,
      data,
      config
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching default combinations:", error);
    if (error.response.status === 403) {
      handleLogoutTokenExpired();
    }
    throw new Error(error.response.data.error);
  }
};

// Save default combinations
const saveDefaultCombinations = async (data, token) => {
  if (!data || !data.bot_id || !data.default_dict) {
    console.error("Invalid data. Please refresh and try again.");
    throw new Error("Invalid data. Please refresh and try again.");
  }

  if (!token) {
    token = JSON.parse(localStorage.getItem("token"));
  }

  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__?.token;
  }

  if (!token) {
    handleLogoutTokenExpired();
    return;
  }

  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  const { bot_id, default_dict, replace, remove } = data;
  const payload = { default_dict };

  if (replace) payload.replace = replace;
  if (remove) payload.remove = remove;

  try {
    const { data: response } = await API.post(
      `/savedefault/${bot_id}`,
      payload,
      config
    );
    return response;
  } catch (error) {
    console.error("Error saving default combinations:", error);
    if (error.response.status === 403) {
      handleLogoutTokenExpired();
    }
    throw new Error(error.response.data.error);
  }
};

// Get finetuning training loss
const getFinetuningTrainingLoss = async (data, token) => {
  if (!data || !data.org_id || !data.bot_id || !data.combination_id) {
    console.error("Invalid data. Please refresh and try again.");
    throw new Error("Invalid data. Please refresh and try again.");
  }

  if (!token) {
    token = JSON.parse(localStorage.getItem("token"));
  }

  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__?.token;
  }

  if (!token) {
    handleLogoutTokenExpired();
    return;
  }

  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  try {
    const response = await API.get(
      `/finetune/training_loss/${data.org_id}/${data.bot_id}/${data.combination_id}`,
      config
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching finetuning training loss:", error);
    if (error.response.status === 403) {
      handleLogoutTokenExpired();
    }
    throw new Error(error.response.data.error);
  }
};

// Cancel finetuning
const cancelFinetuning = async (data, token) => {
  if (!data || !data.orgId || !data.bot_id || !data.combination_id) {
    console.error("Invalid data. Please refresh and try again.");
    throw new Error("Invalid data. Please refresh and try again.");
  }

  if (!token) {
    token = JSON.parse(localStorage.getItem("token"));
  }

  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__?.token;
  }

  if (!token) {
    handleLogoutTokenExpired();
    return;
  }

  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  try {
    const response = await API.get(
      `/cancel_finetuning/${data.orgId}/${data.bot_id}/${data.combination_id}`,
      config
    );
    return response.data;
  } catch (error) {
    console.error("Error cancelling finetuning:", error);
    if (error.response.status === 403) {
      handleLogoutTokenExpired();
    }
    throw new Error(error.response.data.error);
  }
};

//update bot name
// Update app name
const updateAppName = async (data, token) => {
  if (!data || !data.orgId || !data.bot_id) {
    console.error("Invalid data. Please refresh and try again.");
    throw new Error("Invalid data. Please refresh and try again.");
  }

  if (!token) {
    token = JSON.parse(localStorage.getItem("token"));
  }

  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__?.token;
  }

  if (!token) {
    handleLogoutTokenExpired();
    return;
  }

  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  try {
    const response = await API.post(
      `/edit/chatbot_name/${data.orgId}/${data.bot_id}`,
      data,
      config
    );
    return response.data;
  } catch (error) {
    console.error("Error updating app name:", error);
    if (error.response.status === 403) {
      handleLogoutTokenExpired();
    }
    throw new Error(error.response.data.error);
  }
};

// Update golden responses
const updateGoldenResponses = async (data, token) => {
  if (!data || !data.orgId || !data.bot_id) {
    console.error("Invalid data. Please refresh and try again.");
    throw new Error("Invalid data. Please refresh and try again.");
  }

  if (!token) {
    token = JSON.parse(localStorage.getItem("token"));
  }

  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__?.token;
  }

  if (!token) {
    handleLogoutTokenExpired();
    return;
  }

  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  try {
    const response = await API.post(
      `/update_goldenResponses/${data.orgId}/${data.bot_id}`,
      data,
      config
    );
    return response.data;
  } catch (error) {
    console.error("Error updating golden responses:", error);
    if (error.response.status === 403) {
      handleLogoutTokenExpired();
    }
    throw new Error(error.response.data.error);
  }
};

// Update prompt statement settings
const updatePromptStatementSettings = async (data, token) => {
  if (!data || !data.orgId || !data.bot_id) {
    console.error("Invalid data. Please refresh and try again.");
    throw new Error("Invalid data. Please refresh and try again.");
  }

  if (!token) {
    token = JSON.parse(localStorage.getItem("token"));
  }

  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__?.token;
  }

  if (!token) {
    handleLogoutTokenExpired();
    return;
  }

  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  try {
    const response = await API.post(
      `/update/promptStatementSettings/${data.orgId}/${data.bot_id}`,
      data,
      config
    );
    return response.data;
  } catch (error) {
    console.error("Error updating prompt statement settings:", error);
    if (error.response.status === 403) {
      handleLogoutTokenExpired();
    }
    throw new Error(error.response.data.error);
  }
};

//validate neo4j credentials
const validateNeo4jCreds = async (data, token) => {
  if (!token) {
    token = JSON.parse(localStorage.getItem("token"));
  }
  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__.token;
  }
  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
  const response = await API.post(`/validateNeo4j/`, data, config);
  return response.data;
};

// Get Combination Details
const getCombinationDetails = async (data, token) => {
  if (!data || !data.tool_id || !data.type || !data.combo_id) {
    console.error("Invalid data. Please refresh and try again.");
    throw new Error("Invalid data. Please refresh and try again.");
  }

  if (!token) {
    token = JSON.parse(localStorage.getItem("token"));
  }

  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__?.token;
  }

  if (!token) {
    handleLogoutTokenExpired();
    return;
  }

  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  try {
    const response = await API.get(
      `/getCombination/${data.tool_id}/${data.type}/${data.combo_id}`,
      config
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching combination details:", error);
    if (error.response?.status === 403) {
      handleLogoutTokenExpired();
    }
    throw new Error(error.response?.data?.error || "Unknown error");
  }
};

// Generate Questions
const generateQuestions = async (data, token) => {
  if (!data || !data.bot_id || !data.num_of_qa_pairs) {
    console.error("Invalid data. Please refresh and try again.");
    throw new Error("Invalid data. Please refresh and try again.");
  }

  if (!token) {
    token = JSON.parse(localStorage.getItem("token"));
  }

  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__?.token;
  }

  if (!token) {
    handleLogoutTokenExpired();
    return;
  }

  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  try {
    const response = await API.post(
      `/generate_golden_q_and_a/${data.bot_id}`,
      { num_of_qa_pairs: data.num_of_qa_pairs },
      config
    );
    return response.data;
  } catch (error) {
    console.error("Error generating questions:", error);
    if (error.response.status === 403) {
      handleLogoutTokenExpired();
    }
    throw new Error(error.response.data.error);
  }
};

// Get Init Cost
const getInitCost = async (data, token) => {
  if (!data || !data.orgId || !data.userId || !data.bot_id || !data.toolId) {
    console.error("Invalid data. Please refresh and try again.");
    throw new Error("Invalid data. Please refresh and try again.");
  }

  if (!token) {
    token = JSON.parse(localStorage.getItem("token"));
  }

  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__?.token;
  }

  if (!token) {
    handleLogoutTokenExpired();
    return;
  }

  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  try {
    const response = await API.get(
      `/init/cost_and_time/${data.orgId}/${data.userId}/${data.bot_id}/${data.toolId}`,
      config
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching init cost:", error);
    if (error.response.status === 403) {
      handleLogoutTokenExpired();
    }
    throw new Error(error.response.data.error);
  }
};

// Get Bot by ID
const getBotbyID = async (data, token) => {
  if (!data || !data.bot_id) {
    console.error("Invalid data. Please refresh and try again.");
    throw new Error("Invalid data. Please refresh and try again.");
  }

  if (!token) {
    token = JSON.parse(localStorage.getItem("token"));
  }

  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__?.token;
  }

  if (!token) {
    handleLogoutTokenExpired();
    return;
  }

  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  try {
    const response = await API.post(`/botById/${data.bot_id}`, {}, config);
    return response.data;
  } catch (error) {
    console.error("Error fetching bot by ID:", error);
    if (error.response.status === 403) {
      handleLogoutTokenExpired();
    }
    throw new Error(error.response.data.error);
  }
};

export const downloadParsedFiles = async (data, token) => {
  try {
    if (!token) {
      token = JSON.parse(localStorage.getItem("token"));
    }

    const config = {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      responseType: "blob", // Important for downloading files
    };

    const { org_id, bot_id, retrieval_id, comb_id } = data;
    const response = await API.get(
      `/get/parsed_output/${org_id}/${bot_id}/${retrieval_id}/${comb_id}`,
      config
    );

    // Create a Blob from the response and trigger download
    const blob = new Blob([response.data], { type: "application/zip" });
    saveAs(blob, `parsed_files_${comb_id}.zip`);
  } catch (error) {
    console.error("Error downloading file:", error);
  }
};

// Utility to get token from localStorage
const resolveToken = (token) => {
  if (!token) {
    token = JSON.parse(localStorage.getItem("token"));
  }
  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__?.token;
  }
  return token;
};

// Get bots by user ID
const getBotsbyUser = async (id, token) => {
  if (!id) {
    console.error("User ID is required.");
    return [];
  }

  token = resolveToken(token);
  if (!token) {
    handleLogoutTokenExpired();
    return [];
  }

  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  try {
    const response = await API.get(`/get/bot_via_user/${id}`, config);
    return response.data;
  } catch (error) {
    console.error("Error fetching bots by user:", error);
    if (error.response.status === 403) {
      handleLogoutTokenExpired();
    }
    throw new Error(error.response.data.error);
  }
};

// Get bots by user ID
const getDeployedBotsbyUser = async (id, token) => {
  if (!id) {
    console.error("User ID is required.");
    return [];
  }

  token = resolveToken(token);
  if (!token) {
    handleLogoutTokenExpired();
    return [];
  }

  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  try {
    const response = await API.get(`/get/deployed/bot_via_user/${id}`, config);
    return response.data;
  } catch (error) {
    console.error("Error fetching bots by user:", error);
    if (error.response.status === 403) {
      handleLogoutTokenExpired();
    }
    throw new Error(error.response.data.error);
  }
};

// Get evaluation for a batch of combinations
const getCombinationEvalBatch = async (data, token) => {
  if (!data) {
    console.error("Input data is required.");
    return [];
  }

  token = resolveToken(token);
  if (!token) {
    handleLogoutTokenExpired();
    return [];
  }

  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  try {
    const response = await API.post(`/combinations/eval/`, data, config);
    return response.data;
  } catch (error) {
    console.error("Error fetching combination evaluations:", error);
    if (error.response.status === 403) {
      handleLogoutTokenExpired();
    }
    throw new Error(error.response.data.error);
  }
};

// Cache for evaluation results
const evaluationCache = new Map();

// Get evaluation for a specific combination by tool_type and combination_id
const getEvaluationByCombinationId = async (data, token) => {
  if (!data || !data.tool_type || !data.combination_id) {
    console.error("Tool type and combination ID are required.");
    return [];
  }

  // Include previous_combination and iteration in the cache key to differentiate requests
  const cacheKey = `${data.tool_type}_${data.combination_id}_${data.previous_combination || "none"}_${data.iteration ?? "none"}`;

  // Check if we have cached data
  if (evaluationCache.has(cacheKey)) {
    return evaluationCache.get(cacheKey);
  }

  token = resolveToken(token);
  if (!token) {
    handleLogoutTokenExpired();
    return [];
  }

  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  try {
    // This endpoint expects POST with JSON body containing previous_combination and iteration
    const payload = {
      previous_combination: data.previous_combination,
      iteration: data.iteration,
    };

    const response = await API.post(
      `/get/evaluation/${data.tool_type}/${data.combination_id}`,
      payload,
      config
    );

    // Cache the response data for this key
    evaluationCache.set(cacheKey, response.data);

    return response.data;
  } catch (error) {
    console.error("Error fetching evaluation by combination ID:", error);
    if (error.response?.status === 403) {
      handleLogoutTokenExpired();
    }
    throw new Error(error.response?.data?.error || error.message);
  }
};

// Function to clear evaluation cache
const clearEvaluationCache = () => {
  evaluationCache.clear();
};

// Function to clear specific evaluation from cache
const clearSpecificEvaluationFromCache = (tool_type, combination_id) => {
  const cacheKey = `${tool_type}_${combination_id}`;
  if (evaluationCache.has(cacheKey)) {
    evaluationCache.delete(cacheKey);
  }
};

// Function to check if an evaluation is cached
const isEvaluationCached = (tool_type, combination_id) => {
  const cacheKey = `${tool_type}_${combination_id}`;
  return evaluationCache.has(cacheKey);
};

// Function to get cache size
const getEvaluationCacheSize = () => {
  return evaluationCache.size;
};

// get tools by id
const getSpecificToolById = async (data) => {
  let token = JSON.parse(localStorage.getItem("token"));
  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__.token;
  }

  if (!token) {
    handleLogoutTokenExpired();
    return;
  }

  if (!data.toolId || !data.toolType) {
    throw new Error("Invalid data. Please refresh and try again");
  }

  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  try {
    const response = await API.get(
      `/get/${data.toolType}/${data.toolId}`,
      config
    );
    return response.data;
  } catch (error) {
    console.log("Error getting combinations: ", error);
    if (error.response.status === 403) {
      handleLogoutTokenExpired();
    }
    throw new Error(error.response.data.error);
  }
};

const generateAgentPrompts = async (data, token) => {
  if (!data.problem_statement) {
    console.error("Invalid problem statement. Please refresh and try again.");
    throw new Error("Invalid data. Please refresh and try again.");
  }

  if (!token) {
    token = JSON.parse(localStorage.getItem("token"));
  }

  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__?.token;
  }

  if (!token) {
    handleLogoutTokenExpired();
    return;
  }

  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  const currentUser = JSON.parse(localStorage.getItem("user"));
  const user_id = currentUser?._id["$oid"];

  try {
    const response = await API.post(
      `/generate/agent-prompt/${user_id}`,
      data,
      config
    );
    return response.data;
  } catch (error) {
    console.error("Error generating prompts:", error);
    if (error.response.status === 403) {
      handleLogoutTokenExpired();
    }
    throw new Error(error.response.data.error);
  }
};

const getAgentCombinations = async (sentData, token) => {
  if (!sentData) throw new Error("Invalid data");

  const { id, items, from, data } = sentData;

  if (!id || items == null || from == null) {
    throw new Error("Invalid parameters");
  }

  if (!token) {
    token =
      JSON.parse(localStorage.getItem("token")) ||
      JSON.parse(localStorage.getItem("user"))?.token;
  }
  if (!token) return handleLogoutTokenExpired();

  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: "application/json",
    },
  };

  const endpoint = `/agent/combinations/${id}/${items}/${from}`;
  const body = data?.sortOrder || data?.filters ? data : {};

  try {
    const response = await API.post(endpoint, body, config);
    return response.data;
  } catch (error) {
    if (error.response?.status === 403) handleLogoutTokenExpired();
    throw new Error(error.response?.data?.error || error.message);
  }
};

// run agent combination
const runAgentEvals = async (ids, token) => {
  const { orgId, user_id, bot_id, agent_id, combination_id } = ids;
  if (!orgId || !user_id || !bot_id || !agent_id || !combination_id) {
    console.error(
      "Missing required identifiers. Please refresh and try again."
    );
    throw new Error("Missing required identifiers.");
  }

  // resolve token if not passed
  if (!token) {
    token =
      JSON.parse(localStorage.getItem("token")) ||
      JSON.parse(localStorage.getItem("user"))?.token;
  }
  if (!token) {
    handleLogoutTokenExpired();
    return;
  }

  const config = { headers: { Authorization: `Bearer ${token}` } };
  try {
    const response = await API.post(
      `/runeval/agent/${orgId}/${user_id}/${bot_id}/${agent_id}/${combination_id}`,
      {},
      config
    );
    return response.data.combination;
  } catch (error) {
    console.error("Error running agent eval:", error);
    if (error.response?.status === 403) handleLogoutTokenExpired();
    throw new Error(error.response?.data?.error || error.message);
  }
};

// Run parser evaluation
const runParserEvals = async ({
  orgId,
  user_id,
  bot_id,
  parser_id,
  combination_id,
  token,
}) => {
  if (!orgId || !user_id || !bot_id || !parser_id || !combination_id) {
    throw new Error("Missing required parameters for parser evaluation");
  }

  if (!token) {
    token = JSON.parse(localStorage.getItem("token"));
  }
  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__?.token;
  }
  if (!token) {
    handleLogoutTokenExpired();
    return;
  }

  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  try {
    const response = await API.get(
      `/runeval/parser/${orgId}/${user_id}/${bot_id}/${parser_id}/${combination_id}`,
      config
    );
    return response.data;
  } catch (error) {
    console.error("Error running parser evaluation:", error);
    if (error.response?.status === 403) {
      handleLogoutTokenExpired();
    }
    throw new Error(error.response?.data?.error || error.message);
  }
};

const addCombination = async (data, token) => {
  if (!token) {
    token = localStorage.getItem("token")
      ? JSON.parse(localStorage.getItem("token"))
      : null;
  }

  if (!token) {
    throw new Error("Authorization token is missing");
  }

  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  try {
    const response = await API.post(
      `/add/combination/generation/${data.tool_id}`,
      data.payload,
      config
    ); // Empty object as body
    return response.data;
  } catch (error) {
    console.error("Error fetching combinations by ID:", error);
    if (error.response.status === 403) {
      handleLogoutTokenExpired();
    }
    throw error;
  }
};

const updateCombination = async (data, token) => {
  if (!token) {
    token = localStorage.getItem("token")
      ? JSON.parse(localStorage.getItem("token"))
      : null;
  }

  if (!token) {
    throw new Error("Authorization token is missing");
  }

  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  try {
    const response = await API.put(
      `/update/combination/generation/${data.bot_id}`,
      data.payload,
      config
    ); // Empty object as body
    return response.data;
  } catch (error) {
    console.error("Error fetching combinations by ID:", error);
    if (error.response.status === 403) {
      handleLogoutTokenExpired();
    }
    throw error;
  }
};

const getCombinationByID = async (data, token) => {
  if (!token) {
    token = localStorage.getItem("token")
      ? JSON.parse(localStorage.getItem("token"))
      : null;
  }

  if (!token) {
    throw new Error("Authorization token is missing");
  }

  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  try {
    const response = await API.get(
      `/get_combination_by_id/${data.type}/${data.comb_id}`,
      config
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching combinations by ID:", error);
    if (error.response.status === 403) {
      localStorage.removeItem("user");
      localStorage.removeItem("token");
      alert("Session Expired. Please login again.");
      window.location.href = "/";
    }
    throw error;
  }
};

// Fetch evaluation metrics from S3 for a given tool type and combination
const getEvaluationMetrics = async (data, token) => {
  if (!data || !data.tool_type || !data.combination_id) {
    console.error("tool_type and combination_id are required.");
    throw new Error("Invalid data. Please refresh and try again.");
  }

  // Resolve token if not provided
  if (!token) {
    token = JSON.parse(localStorage.getItem("token"));
  }
  if (!token) {
    const __USER__ = JSON.parse(localStorage.getItem("user"));
    token = __USER__?.token;
  }
  if (!token) {
    handleLogoutTokenExpired();
    return;
  }

  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };

  const payload = data.payload || {};

  try {
    const response = await API.post(
      `/get/evaluation/metrics/${data.tool_type}/${data.combination_id}`,
      payload,
      config
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching evaluation metrics:", error);
    if (error.response?.status === 403) {
      handleLogoutTokenExpired();
    }
    throw new Error(error.response?.data?.error || error.message);
  }
};

const chatbotService = {
  createBot,
  getBotbyOrg,
  getBotbyOrgTemplates,
  saveOptimizedBot,
  getCombinationFilters,
  getCombinations,
  getgenerationCombinations,
  getFinetuningCombinations,
  getParserCombinations,
  runEvals,
  runGenerationEvals,
  runFinetuningEvals,
  startFinetuning,
  getFinetuningStatus,
  deleteChatbot,
  deleteFiles,
  initializeCombinations,
  initializeCombinationsSync,
  generatePrompts,
  generateQueryWithProblemStatement,
  initializeMutations,
  get_selected_combinations,
  makeDefault,
  uploadSources,
  getDataTypes,
  getMetadataDescription,
  initializeEmbeddingFinetuning,
  getDefaultCombinations,
  saveDefaultCombinations,
  getFinetuningTrainingLoss,
  cancelFinetuning,
  updateAppName,
  updateGoldenResponses,
  updatePromptStatementSettings,
  validateNeo4jCreds,
  getCombinationDetails,
  generateQuestions,
  getInitCost,
  getBotbyID,
  downloadParsedFiles,
  getBotsbyUser,
  getDeployedBotsbyUser,
  getCombinationEvalBatch,
  getEvaluationByCombinationId,
  clearEvaluationCache,
  clearSpecificEvaluationFromCache,
  isEvaluationCached,
  getEvaluationCacheSize,
  cancelBotUpload,
  getSpecificToolById,
  generateAgentPrompts,
  getAgentCombinations,
  runAgentEvals,
  runParserEvals,
  addCombination,
  updateCombination,
  getCombinationByID,
  getEvaluationMetrics,
};

export default chatbotService;
